import type { PlaceRoom, PlaceSize } from "../../ui/kit/place.ts";

/**
 * The phone form's room (ADR-138's amendment of 2026-10-31, decision 9; the author, 2026-10-31: "use the full available
 * height of the landscape screen"): the whole HUD area less a small margin and the device's safe-area insets (the notch
 * and rounded corners), so the window is as tall as the screen allows rather than starting under the HUD's top band.
 */

/**
 * The margin kept on each side, in logical pixels.
 */
const MARGIN = 2;

/**
 * The device's safe-area insets in logical pixels.
 */
export interface SafeInsets {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * No insets: a desktop browser or one that reports none.
 */
export const NO_INSETS: SafeInsets = Object.freeze({ left: 0, top: 0, right: 0, bottom: 0 });

/**
 * Measures the safe-area insets under an element, in its logical pixels (`--px`), with a probe that holds the four
 * `env(safe-area-inset-*)` values.
 *
 * @param host An element under the HUD's layer, where `--px` is defined.
 * @returns The insets, rounded up to whole logical pixels; none when the page cannot measure them.
 */
export function measureSafeInsets(host: HTMLElement | null): SafeInsets {
  if (host === null || typeof getComputedStyle !== "function") {
    return NO_INSETS;
  }
  const probe = document.createElement("div");
  probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;width:var(--px);padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)";
  host.append(probe);
  const style = getComputedStyle(probe);
  const px = Number.parseFloat(style.width) || 1;
  const read = (value: string): number => Math.ceil((Number.parseFloat(value) || 0) / px);
  const insets = { left: read(style.paddingLeft), top: read(style.paddingTop), right: read(style.paddingRight), bottom: read(style.paddingBottom) };
  probe.remove();
  return insets;
}

/**
 * The room the phone form fills.
 *
 * @param area The HUD's logical area.
 * @param insets The safe-area insets.
 * @returns The room.
 */
export function phoneRoom(area: PlaceSize, insets: SafeInsets): PlaceRoom {
  return { left: MARGIN + insets.left, top: MARGIN + insets.top, right: area.width - MARGIN - insets.right, bottom: area.height - MARGIN - insets.bottom };
}
